import { useEffect, useRef, useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import type { OrganisationDeletionStatus, OrganisationWithCounts } from '../../../../../../backend/src/types/types';
import { Breadcrumb } from '../../../components/breadcrumb';
import { ScrollableTable } from '../../../components/table/scrollable-table';
import { useGetAllOrganisations } from '@/api/queries/useGetOrganisations';
import { PermissionGate, useHasPermission } from '@/hooks/useHasPermission';
import { usePlatformPermissionCheck } from '@/hooks/usePlatformPermissionCheck';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { OverflowMenu } from '@/new/components/overflow-menu';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { DropdownMenuItem } from '@/new/components/shadcn/dropdown-menu';
import { CellWithCopy } from '@/new/components/table/table-cell-with-copy';
import { CreateOrganisationDialog } from '@/new/pages/admin/organisations/create-organisation-dialog';
import { DeleteOrganisationDialog } from '@/new/pages/admin/organisations/delete-organisation-dialog';

const fallbackTableData: OrganisationWithCounts[] = [];
const columnHelper = createColumnHelper<OrganisationWithCounts>();

function OrganisationDeletionStatusChip({ status }: { status: OrganisationDeletionStatus | null }) {
  if (status === null) return null;
  if (status === 'failed') {
    return <Chip className="bg-red-50 text-red-700 ring-1 ring-red-200 ring-inset">Failed</Chip>;
  }
  if (status === 'queued') {
    return <Chip className="bg-blue-50 text-blue-700 ring-1 ring-blue-200 ring-inset">Queued</Chip>;
  }
  return <Chip className="bg-blue-50 text-blue-700 ring-1 ring-blue-200 ring-inset">Deleting</Chip>;
}

const columns = [
  columnHelper.accessor('name', {
    header: () => <span>Organisation Name</span>,
    cell: (info) => <span className="line-clamp-1 min-w-48 break-all">{info.getValue()}</span>,
  }),
  columnHelper.accessor('id', {
    header: () => <span>Organisation Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-36 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('userCount', {
    header: () => <span>No. of Users</span>,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('matterCount', {
    header: () => <span>No. of Matters</span>,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('deletionStatus', {
    header: () => <span>Deletion Status</span>,
    cell: (info) => <OrganisationDeletionStatusChip status={info.getValue()} />,
  }),
  columnHelper.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    cell: (props) => (
      <div onClick={(event) => event.stopPropagation()}>
        <OrganisationRowActions organisation={props.row.original} />
      </div>
    ),
  }),
];

function OrganisationRowActions({ organisation }: { organisation: OrganisationWithCounts }) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  if (
    organisation.isGlobalAdmin ||
    organisation.deletionStatus === 'queued' ||
    organisation.deletionStatus === 'inProgress'
  ) {
    return null;
  }

  return (
    <PermissionGate permission={Permission.PlatformDeleteOrganisations}>
      <OverflowMenu ariaLabel={`Actions for ${organisation.name}`}>
        <DropdownMenuItem
          onSelect={() => setDeleteDialogOpen(true)}
          className="text-red-600 focus:bg-red-50 focus:text-red-700"
        >
          <Icon className="lucide--trash-2" />
          {organisation.deletionStatus === 'failed' ? 'Retry Deletion' : 'Delete Organisation'}
        </DropdownMenuItem>
      </OverflowMenu>
      <DeleteOrganisationDialog
        organisationId={organisation.id}
        organisationName={organisation.name}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      />
    </PermissionGate>
  );
}

export function Admin() {
  const navigate = useNavigate();
  const { hasPermission } = useHasPermission();
  usePlatformPermissionCheck([
    Permission.PlatformManageOrganisations,
    Permission.PlatformDeleteOrganisations,
    Permission.PlatformManageBilling,
    Permission.PlatformManageUsers,
    Permission.PlatformViewUsage,
    Permission.PlatformManageRuns,
    Permission.PlatformViewFeedback,
  ]);
  const { data: organisations, isLoading } = useGetAllOrganisations();
  const deletingOrganisations = useRef(new Map<string, string>());

  useEffect(() => {
    if (organisations === undefined) return;

    const currentIds = new Set(organisations.map(({ id }) => id));
    for (const [id, name] of deletingOrganisations.current) {
      if (!currentIds.has(id)) {
        toast.success(`${name} was deleted`);
        deletingOrganisations.current.delete(id);
      }
    }

    for (const organisation of organisations) {
      if (organisation.deletionStatus === 'queued' || organisation.deletionStatus === 'inProgress') {
        deletingOrganisations.current.set(organisation.id, organisation.name);
      }
    }
  }, [organisations]);

  const [globalFilter, setGlobalFilter] = useState('');

  const organisationDetailSuffix = hasPermission(Permission.PlatformManageOrganisations)
    ? ''
    : hasPermission(Permission.PlatformManageBilling)
      ? '/billing'
      : hasPermission(Permission.PlatformManageUsers)
        ? '/users'
        : hasPermission(Permission.PlatformViewFeedback)
          ? '/feedback'
          : hasPermission(Permission.PlatformManageRuns)
            ? '/runs'
            : null;

  const table = useReactTable({
    data: organisations ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [
        {
          id: 'userCount',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
    },
  });

  return (
    <>
      <Breadcrumb items={[{ href: '/admin/organisations', label: 'Organisations', active: true }]} />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Organisations" />
        </PageHeader>
      </PageContainer>

      <ScrollableTable
        table={table}
        plural="organisations"
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        loading={isLoading}
        rowOnClick={
          organisationDetailSuffix === null
            ? undefined
            : (row) => navigate(`/admin/organisations/${row.original.id}${organisationDetailSuffix}`)
        }
        additionalButtons={
          <PermissionGate permission={Permission.PlatformManageOrganisations} uiMode="disabled">
            <CreateOrganisationDialog />
          </PermissionGate>
        }
      />
    </>
  );
}
